import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  InputAdornment,
  FormControl,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  SelectChangeEvent,
} from '@mui/material';
import { getSocket } from '../services/realtimeService';
import { useNotification } from '../components/layouts/Layout';
import { Search } from '@mui/icons-material';
import ContractorsTab from '../components/database/ContractorsTab';
import GoodsAndServicesTab from '../components/database/GoodsAndServicesTab';
import { getAllProjects, Project } from '../services/projectService';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`database-tabpanel-${index}`}
      aria-labelledby={`database-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const DatabasePage: React.FC = () => {
  const [tabValue, setTabValue] = useState(0); // Domyślnie wybrana zakładka "Kontrahenci"
  const [searchText, setSearchText] = useState('');
  const [nipText, setNipText] = useState('');
  const [location, setLocation] = useState('');
  const [industry, setIndustry] = useState(''); // Dla ContractorsTab
  const [selectedProject, setSelectedProject] = useState(''); // Dla GoodsAndServicesTab
  const [projects, setProjects] = useState<Project[]>([]);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const dateFromRef = useRef<HTMLInputElement>(null);
  const dateToRef = useRef<HTMLInputElement>(null);
  const { showNotification } = useNotification();

  const handleTabChange = (newValue: number) => {
    setTabValue(newValue);
    try { getSocket().emit('ui:tab:set', { page: 'database', tab: newValue }); } catch {}
  };

  // Listener dla synchronizacji z obserwowanym
  useEffect(() => {
    const onExternal = (e: any) => {
      if (e?.detail?.page === 'database' && typeof e.detail.tab === 'number') setTabValue(e.detail.tab);
    };
    window.addEventListener('ui:tab:set:external', onExternal as any);
    return () => window.removeEventListener('ui:tab:set:external', onExternal as any);
  }, []);


  const handleProjectChange = (event: SelectChangeEvent) => {
    setSelectedProject(event.target.value as string);
  };

  const handleIndustryChange = (event: SelectChangeEvent) => {
    setIndustry(event.target.value as string);
  };

  // Pobieranie projektów przy załadowaniu komponentu
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const projectsData = await getAllProjects();
        setProjects(projectsData);
      } catch (error) {
        console.error('Błąd podczas pobierania projektów:', error);
        showNotification('Nie udało się pobrać listy projektów', 'error');
      }
    };
    fetchProjects();
  }, [showNotification]);

  return (
    <Box sx={{ bgcolor: '#FAFAFA', mt: '20px', position: 'relative' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0, mb: 0, minHeight: 40 }}>
        <Typography
          component="h1"
          sx={{
            fontFamily: 'Roboto, sans-serif',
            fontSize: '20px',
            lineHeight: '24px',
            fontWeight: 400,
            color: '#020242',
            m: 0
          }}
        >
          Bazy danych
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {/* miejsce na ewentualne akcje w nagłówku (np. dodaj) */}
        </Box>
      </Box>

      {/* Pasek zakładek głównych – styl jak na stronie projektu (bez etykiet „w budowie”) */}
      {(() => {
        const headerTabs = [
          'Kontrahenci',
          'Towary i usłygi',
          'Zapytania',
          'Zamówienia',
          'Oferty dostawców',
          'KZM',
        ];
        return (
          <Box
            data-tab-scope="main"
            sx={{ mt: '28px', mb: 2, borderBottom: '1px solid #EDEDED', display: 'flex', gap: 3, alignItems: 'flex-end' }}
          >
            {headerTabs.map((label, idx) => (
              <Box
                key={label}
                data-tab="main"
                aria-selected={tabValue === idx ? 'true' : 'false'}
                onClick={() => handleTabChange(idx)}
                sx={{
                  position: 'relative',
                  pb: 1,
                  cursor: 'pointer',
                  fontFamily: 'Roboto, sans-serif',
                  fontSize: '16px',
                  color: '#020242',
                  fontWeight: tabValue === idx ? 500 : 400,
                  userSelect: 'none',
                  display: 'block'
                }}
              >
                <Box component="span">{label}</Box>
                {tabValue === idx && (
                  <Box sx={{ position: 'absolute', left: 0, right: 0, bottom: -1, height: 2, bgcolor: '#000000' }} />
                )}
              </Box>
            ))}
          </Box>
        );
      })()}

      <Box 
        sx={{ 
          display: 'flex', 
          flexWrap: 'wrap', 
          gap: 1, 
          my: 2,
          py: 1
        }}
      >
        <TextField
          size="small"
          placeholder={tabValue === 1 ? "Szukaj towaru lub usługi" : "Szukaj kontrahenta..."}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && tabValue === 0) {
              // Wymuszenie ponownego wyszukiwania po naciśnięciu Enter
              const searchEvent = new Event('search');
              window.dispatchEvent(searchEvent);
            }
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
            style: { height: '32px' }
          }}
          sx={{ width: '280px' }}
        />
        {tabValue === 1 ? (
          <FormControl size="small" sx={{ width: '280px' }}>
            <Select
              value={selectedProject}
              onChange={handleProjectChange}
              displayEmpty
              renderValue={selectedProject !== '' ? undefined : () => 'Wybierz Projekt'}
              sx={{ height: '32px' }}
            >
              <MenuItem value="">Wszystkie</MenuItem>
              {projects.map((project) => (
                <MenuItem key={project.project_id} value={project.project_id}>
                  {project.project_id} - {project.project_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : (
          <FormControl size="small" sx={{ width: '280px' }}>
            <Select
              value={industry}
              onChange={handleIndustryChange}
              displayEmpty
              renderValue={(selected) => {
                if (selected === '' || !selected) {
                  return 'Wybierz branżę';
                }
                return selected as string;
              }}
              sx={{ height: '32px' }}
            >
              <MenuItem value="">Wszystkie</MenuItem>
              <MenuItem value="Tynkarstwo">Tynkarstwo</MenuItem>
              <MenuItem value="Murarstwo">Murarstwo</MenuItem>
              <MenuItem value="Szklarstwo">Szklarstwo</MenuItem>
              <MenuItem value="Dekarstwo">Dekarstwo</MenuItem>
              <MenuItem value="Elektryka">Elektryka</MenuItem>
              <MenuItem value="Hydraulika">Hydraulika</MenuItem>
              <MenuItem value="Stolarstwo">Stolarstwo</MenuItem>
              <MenuItem value="Architektura wnętrz">Architektura wnętrz</MenuItem>
            </Select>
          </FormControl>
        )}
        <TextField
          size="small"
          type="date"
          placeholder="Data od"
          value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
          inputRef={dateFromRef}
          InputProps={{
            startAdornment: (
              <InputAdornment 
                position="start" 
                sx={{ cursor: 'pointer' }}
                onClick={() => { try { (dateFromRef.current as any)?.showPicker?.(); } catch { dateFromRef.current?.focus(); } }}
              >
                <Box component="img" src="/assets/filters/calendar_today.svg" alt="calendar" />
              </InputAdornment>
            )
          }}
          InputLabelProps={{ shrink: true }}
          sx={{
            width: '200px',
            '& .MuiOutlinedInput-root': { height: 36, backgroundColor: '#fff', borderRadius: '4px' },
            '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d4d4d9' },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#595966' },
            '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#595966 !important' },
            '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: 'none', outline: 'none' },
            '& .MuiInputBase-input': { fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: dateFrom ? '#2D2D32' : '#B9B9B9' },
            '& input[type="date"]::-webkit-calendar-picker-indicator': { opacity: 0, display: 'none' },
            '& input::-ms-clear': { display: 'none' }
          }}
        />
        <TextField
          size="small"
          type="date"
          placeholder="Data do"
          value={dateTo}
          onChange={(e) => setDateTo(e.target.value)}
          inputRef={dateToRef}
          InputProps={{
            startAdornment: (
              <InputAdornment 
                position="start" 
                sx={{ cursor: 'pointer' }}
                onClick={() => { try { (dateToRef.current as any)?.showPicker?.(); } catch { dateToRef.current?.focus(); } }}
              >
                <Box component="img" src="/assets/filters/calendar_today.svg" alt="calendar" />
              </InputAdornment>
            )
          }}
          InputLabelProps={{ shrink: true }}
          sx={{
            width: '200px',
            '& .MuiOutlinedInput-root': { height: 36, backgroundColor: '#fff', borderRadius: '4px' },
            '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d4d4d9' },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#595966' },
            '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#595966 !important' },
            '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: 'none', outline: 'none' },
            '& .MuiInputBase-input': { fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: dateTo ? '#2D2D32' : '#B9B9B9' },
            '& input[type="date"]::-webkit-calendar-picker-indicator': { opacity: 0, display: 'none' },
            '& input::-ms-clear': { display: 'none' }
          }}
        />
      </Box>

      <TabPanel value={tabValue} index={0}>
        <ContractorsTab 
          searchText={searchText}
          location={location}
          nipText={nipText}
          industry={industry}
        />
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <GoodsAndServicesTab selectedProject={selectedProject} />
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <Box sx={{ p: 2 }}>
          <Typography>Moduł Zapytania w przygotowaniu</Typography>
        </Box>
      </TabPanel>

      <TabPanel value={tabValue} index={3}>
        <Box sx={{ p: 2 }}>
          <Typography>Moduł Zamówienia w przygotowaniu</Typography>
        </Box>
      </TabPanel>

      <TabPanel value={tabValue} index={4}>
        <Box sx={{ p: 2 }}>
          <Typography>Moduł Oferty dostawców w przygotowaniu</Typography>
        </Box>
      </TabPanel>

      <TabPanel value={tabValue} index={5}>
        <Box sx={{ p: 2 }}>
          <Typography>Moduł KZM w przygotowaniu</Typography>
        </Box>
      </TabPanel>
    </Box>
  );
};

export default DatabasePage;
